/**
 * Normalização e similaridade de texto, para a detecção de duplicidade.
 *
 * "Cabo USB-C 2m" e "CABO USB C 2 METROS" são o mesmo produto para quem
 * cadastra e strings completamente diferentes para o computador. Comparar sem
 * normalizar não encontra nada.
 */

/** Minúsculas, sem acento, sem pontuação, espaços colapsados. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function bigramas(texto: string): Map<string, number> {
  const mapa = new Map<string, number>();
  for (let i = 0; i < texto.length - 1; i++) {
    const par = texto.slice(i, i + 2);
    mapa.set(par, (mapa.get(par) ?? 0) + 1);
  }
  return mapa;
}

/**
 * Coeficiente de Dice sobre bigramas: 0 = nada em comum, 1 = idêntico.
 *
 * Escolhido em vez de distância de edição porque é indiferente à ordem das
 * palavras — "Fone Bluetooth XPTO" e "XPTO Fone Bluetooth" pontuam alto, que é
 * o comportamento desejado num cadastro preenchido por pessoas diferentes.
 */
export function similaridade(a: string, b: string): number {
  const x = normalizar(a);
  const y = normalizar(b);
  if (x === y) return 1;
  if (x.length < 2 || y.length < 2) return 0;

  const bx = bigramas(x);
  const by = bigramas(y);
  let comuns = 0;
  let totalX = 0;
  let totalY = 0;

  for (const [par, n] of bx) {
    totalX += n;
    comuns += Math.min(n, by.get(par) ?? 0);
  }
  for (const n of by.values()) totalY += n;

  return (2 * comuns) / (totalX + totalY);
}
